/**
 * Menu orizzontale desktop (style3).
 * Isolato dal drawer full-menu: non modifica FullMenu né header.js.
 */
if (!customElements.get("horizontal-menu")) {
  class HorizontalMenu extends HTMLElement {
    constructor() {
      super();
      this.closeTimeout = null;
      this.items = [];
      this.onDocumentKeyupHandler = this.onDocumentKeyup.bind(this);
      this.onDocumentClickHandler = this.onDocumentClick.bind(this);
      this.onFocusOutHandler = this.onFocusOut.bind(this);
    }

    connectedCallback() {
      this.header = this.closest(".header");
      this.items = Array.from(
        this.querySelectorAll(".horizontal-menu__item.has-children")
      );

      this.items.forEach((item) => {
        const trigger = item.querySelector(":scope > .horizontal-menu__link");
        const dropdown = item.querySelector(":scope > .horizontal-menu__dropdown");

        if (!trigger || !dropdown) return;

        item.addEventListener("mouseenter", () => this.openItem(item));
        item.addEventListener("mouseleave", () => this.scheduleClose(item));
        dropdown.addEventListener("mouseenter", () => this.clearCloseTimeout());
        dropdown.addEventListener("mouseleave", () => this.scheduleClose(item));

        trigger.addEventListener("focus", () => this.openItem(item));
        trigger.addEventListener("keydown", (event) =>
          this.onTriggerKeydown(item, event)
        );

        const nestedItems = item.querySelectorAll(
          ".horizontal-menu__dropdown-item.has-children"
        );
        nestedItems.forEach((nestedItem) => {
          const nestedTrigger = nestedItem.querySelector(
            ":scope > .horizontal-menu__dropdown-link"
          );
          const nestedDropdown = nestedItem.querySelector(
            ":scope > .horizontal-menu__dropdown--nested"
          );

          if (!nestedTrigger || !nestedDropdown) return;

          nestedItem.addEventListener("mouseenter", () =>
            this.openNestedItem(nestedItem)
          );
          nestedItem.addEventListener("mouseleave", () =>
            this.closeNestedItem(nestedItem)
          );
          nestedTrigger.addEventListener("focus", () =>
            this.openNestedItem(nestedItem)
          );
        });
      });

      this.addEventListener("focusout", this.onFocusOutHandler);
      document.addEventListener("keyup", this.onDocumentKeyupHandler);
      document.addEventListener("click", this.onDocumentClickHandler);
    }

    disconnectedCallback() {
      document.removeEventListener("keyup", this.onDocumentKeyupHandler);
      document.removeEventListener("click", this.onDocumentClickHandler);
      this.removeEventListener("focusout", this.onFocusOutHandler);
      this.clearCloseTimeout();
    }

    openItem(item) {
      this.clearCloseTimeout();
      this.closeSiblingMenus();
      this.items.forEach((menuItem) => {
        if (menuItem !== item) {
          this.closeItem(menuItem);
        }
      });
      item.classList.add("is-open");
      const trigger = item.querySelector(":scope > .horizontal-menu__link");
      trigger?.setAttribute("aria-expanded", "true");
    }

    closeSiblingMenus() {
      this.header?.querySelectorAll("horizontal-menu").forEach((menuEl) => {
        if (menuEl !== this && typeof menuEl.closeAllItems === "function") {
          menuEl.closeAllItems();
        }
      });
    }

    closeItem(item) {
      item.classList.remove("is-open");
      const trigger = item.querySelector(":scope > .horizontal-menu__link");
      trigger?.setAttribute("aria-expanded", "false");

      item
        .querySelectorAll(".horizontal-menu__dropdown-item.has-children.is-open")
        .forEach((nestedItem) => this.closeNestedItem(nestedItem));
    }

    closeAllItems() {
      this.items.forEach((item) => this.closeItem(item));
    }

    openNestedItem(nestedItem) {
      const parentDropdown = nestedItem.closest(".horizontal-menu__dropdown");
      parentDropdown
        ?.querySelectorAll(".horizontal-menu__dropdown-item.has-children.is-open")
        .forEach((openItem) => {
          if (openItem !== nestedItem) this.closeNestedItem(openItem);
        });

      nestedItem.classList.add("is-open");
      nestedItem
        .querySelector(":scope > .horizontal-menu__dropdown-link")
        ?.setAttribute("aria-expanded", "true");
    }

    closeNestedItem(nestedItem) {
      nestedItem.classList.remove("is-open");
      nestedItem
        .querySelector(":scope > .horizontal-menu__dropdown-link")
        ?.setAttribute("aria-expanded", "false");
    }

    scheduleClose(item) {
      this.clearCloseTimeout();
      const dropdown = item.querySelector(":scope > .horizontal-menu__dropdown");
      this.closeTimeout = window.setTimeout(() => {
        const hoveringItem = item.matches(":hover");
        const hoveringDropdown = dropdown?.matches(":hover");
        if (!hoveringItem && !hoveringDropdown) {
          this.closeItem(item);
        }
      }, 120);
    }

    clearCloseTimeout() {
      if (this.closeTimeout) {
        window.clearTimeout(this.closeTimeout);
        this.closeTimeout = null;
      }
    }

    onTriggerKeydown(item, event) {
      const key = event.code?.toUpperCase();

      if (key === "ESCAPE") {
        this.closeItem(item);
        item.querySelector(":scope > .horizontal-menu__link")?.focus();
        return;
      }

      if (key === "ENTER" || key === "SPACE") {
        if (!item.classList.contains("has-children")) return;

        event.preventDefault();
        if (item.classList.contains("is-open")) {
          this.closeItem(item);
        } else {
          this.openItem(item);
          item
            .querySelector(":scope > .horizontal-menu__dropdown .horizontal-menu__dropdown-link")
            ?.focus();
        }
      }

      if (key === "ARROWDOWN" && item.classList.contains("has-children")) {
        event.preventDefault();
        this.openItem(item);
        item
          .querySelector(":scope > .horizontal-menu__dropdown .horizontal-menu__dropdown-link")
          ?.focus();
      }
    }

    onFocusOut(event) {
      if (this.contains(event.relatedTarget)) return;
      this.closeAllItems();
    }

    onDocumentKeyup(event) {
      if (event.code?.toUpperCase() === "ESCAPE") {
        this.closeAllItems();
      }
    }

    onDocumentClick(event) {
      if (!this.contains(event.target)) {
        this.closeAllItems();
      }
    }
  }

  customElements.define("horizontal-menu", HorizontalMenu);
}
